import React, { useEffect, useRef, useState } from "react";

/**
 * "Updated 2 min ago · refreshing…" for the visible tab, plus a short "New
 * data" pulse when a refetch lands numbers that differ from what was shown.
 *
 * Age = the OLDEST of the tab's cards: the server's `computed_at` when the
 * snapshot carries one (that is how old the numbers really are), else when the
 * browser received it. React Query's structural sharing keeps the same data
 * reference when a refetch returns identical numbers, so a reference change is
 * a real change.
 */

const agoLabel = (ms, now = Date.now()) => {
  if (!ms) return null;
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
};

const ageOf = (q) => {
  const c = q?.data?.computed_at ? Date.parse(q.data.computed_at) : NaN;
  return Number.isFinite(c) ? c : q?.dataUpdatedAt || 0;
};

const PULSE_MS = 4000;

export default function Freshness({ queries }) {
  const list = (queries || []).filter(Boolean);
  const shown = list.filter((q) => q.data !== undefined);
  const refreshing = list.some((q) => q.isFetching);
  const failed = shown.some((q) => q.isError);
  const stale = shown.some((q) => q.data?.stale === true);
  const oldest = shown.length ? Math.min(...shown.map(ageOf).filter(Boolean)) : 0;

  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  // "New data": any card's data reference changed after it had data.
  const prev = useRef(null);
  const [pulse, setPulse] = useState(false);
  const timer = useRef(null);
  // A period switch shows the previous period's numbers (isPreviousData) — the
  // new period landing is not "new data", so those slots don't count.
  const refs = list.map((q) => (q.isPreviousData ? undefined : q.data));
  // A data change always comes with a new dataUpdatedAt — that is the trigger;
  // the timer lives in a ref so a re-render never cancels it.
  const sig = list.map((q) => `${q.dataUpdatedAt}:${q.isPreviousData ? 1 : 0}`).join("|");
  useEffect(() => {
    const before = prev.current;
    prev.current = refs;
    if (!before || before.length !== refs.length) return;
    const changed = refs.some((d, i) => before[i] !== undefined && d !== undefined && d !== before[i]);
    if (!changed) return;
    setPulse(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setPulse(false), PULSE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `refs` is read through `sig`
  }, [sig]);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!shown.length) return null;
  const ago = Number.isFinite(oldest) && oldest > 0 ? agoLabel(oldest) : null;
  let tail = null;
  if (refreshing) tail = "refreshing…";
  else if (failed) tail = "couldn't refresh, showing saved numbers";
  else if (stale) tail = "update queued";

  return (
    <span className={`dash-fresh${refreshing ? " is-refreshing" : ""}`} role="status" aria-live="polite" data-testid="dash-freshness">
      <span className="dash-fresh__dot" aria-hidden="true" />
      <span className="dash-fresh__text">
        {ago ? `Updated ${ago}` : "Updated"}
        {tail ? ` · ${tail}` : ""}
      </span>
      {pulse ? <span className="dash-fresh__new">New data</span> : null}
    </span>
  );
}
